import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Plus, Loader2, Trash2, Copy, Check, X, Star, Pencil, Layers, MessageSquare,
} from "lucide-react";

const PUBLISHED_URL = "https://www.fitawrari.com";

type Group = { id: string; name: string; sort_order: number; created_at: string };
type Feedback = {
  id: string; token: string; student_name: string | null;
  feedback_text: string | null; rating: number; is_submitted: boolean;
  is_approved: boolean; created_at: string; submitted_at: string | null;
};

type Props = { sessionToken: string };

const GroupsAndFeedbackTab = ({ sessionToken }: Props) => {
  const { toast } = useToast();

  // Groups state
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [groupProcessing, setGroupProcessing] = useState<string | null>(null);

  // Feedback state
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [editFeedback, setEditFeedback] = useState<Feedback | null>(null);
  const [editName, setEditName] = useState("");
  const [editText, setEditText] = useState("");
  const [editRating, setEditRating] = useState(5);

  // Groups functions
  const fetchGroups = async () => {
    setGroupsLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list_groups", sessionToken },
      });
      if (res.data?.groups) setGroups(res.data.groups);
    } catch {
      toast({ title: "Failed to load groups", variant: "destructive" });
    } finally { setGroupsLoading(false); }
  };

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) return;
    setGroupProcessing("new");
    try {
      await supabase.functions.invoke("manage-certificates", {
        body: { action: "add_group", sessionToken, name: newGroupName.trim() },
      });
      setNewGroupName("");
      toast({ title: "Group added" });
      fetchGroups();
    } catch {
      toast({ title: "Failed to add group", variant: "destructive" });
    } finally { setGroupProcessing(null); }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !editGroupName.trim()) return;
    setGroupProcessing(editingGroup.id);
    try {
      await supabase.functions.invoke("manage-certificates", {
        body: { action: "update_group", sessionToken, groupId: editingGroup.id, name: editGroupName.trim() },
      });
      toast({ title: "Group updated" });
      setEditingGroup(null);
      fetchGroups();
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    } finally { setGroupProcessing(null); }
  };

  const handleDeleteGroup = async (id: string) => {
    setGroupProcessing(id);
    try {
      await supabase.functions.invoke("manage-certificates", {
        body: { action: "delete_group", sessionToken, groupId: id },
      });
      toast({ title: "Group deleted" });
      fetchGroups();
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    } finally { setGroupProcessing(null); }
  };

  // Feedback functions
  const fetchFeedbacks = async () => {
    setFeedbackLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list_feedbacks", sessionToken },
      });
      if (res.data?.feedbacks) setFeedbacks(res.data.feedbacks);
    } catch {
      toast({ title: "Failed to load feedbacks", variant: "destructive" });
    } finally { setFeedbackLoading(false); }
  };

  const handleCreateLink = async () => {
    setCreating(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "create_feedback_link", sessionToken },
      });
      if (res.data?.success) {
        const link = `${PUBLISHED_URL}/feedback/${res.data.token}`;
        await navigator.clipboard.writeText(link);
        toast({ title: "Feedback link created & copied!", description: link });
        fetchFeedbacks();
      }
    } catch {
      toast({ title: "Failed to create link", variant: "destructive" });
    } finally { setCreating(false); }
  };

  const copyLink = (token: string) => {
    const link = `${PUBLISHED_URL}/feedback/${token}`;
    navigator.clipboard.writeText(link);
    toast({ title: "Link copied!" });
  };

  const handleToggleApproval = async (id: string, currentApproval: boolean) => {
    setProcessingId(id);
    try {
      await supabase.functions.invoke("manage-certificates", {
        body: { action: "toggle_feedback_approval", sessionToken, feedbackId: id, is_approved: !currentApproval },
      });
      toast({ title: !currentApproval ? "Feedback approved — now visible on website" : "Feedback hidden from website" });
      fetchFeedbacks();
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    } finally { setProcessingId(null); }
  };

  const handleDeleteFeedback = async (id: string) => {
    setProcessingId(id);
    try {
      await supabase.functions.invoke("manage-certificates", {
        body: { action: "delete_feedback", sessionToken, feedbackId: id },
      });
      toast({ title: "Feedback deleted" });
      fetchFeedbacks();
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    } finally { setProcessingId(null); }
  };

  const openEdit = (f: Feedback) => {
    setEditFeedback(f);
    setEditName(f.student_name || "");
    setEditText(f.feedback_text || "");
    setEditRating(f.rating || 5);
  };

  const handleSaveEdit = async () => {
    if (!editFeedback) return;
    setProcessingId(editFeedback.id);
    try {
      await supabase.functions.invoke("manage-certificates", {
        body: {
          action: "update_feedback", sessionToken,
          feedbackId: editFeedback.id,
          student_name: editName, feedback_text: editText, rating: editRating,
        },
      });
      toast({ title: "Feedback updated" });
      setEditFeedback(null);
      fetchFeedbacks();
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    } finally { setProcessingId(null); }
  };

  useEffect(() => { fetchGroups(); fetchFeedbacks(); }, []);

  return (
    <div className="space-y-4">
      <Tabs defaultValue="groups">
        <TabsList>
          <TabsTrigger value="groups" className="flex items-center gap-1.5">
            <Layers className="w-4 h-4" /> Groups
          </TabsTrigger>
          <TabsTrigger value="feedback" className="flex items-center gap-1.5">
            <MessageSquare className="w-4 h-4" /> Feedback
          </TabsTrigger>
        </TabsList>

        {/* Groups Tab */}
        <TabsContent value="groups">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Certificate Groups</h2>
            </div>
            <div className="flex gap-2">
              <Input placeholder="New group name..." value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddGroup()} className="max-w-xs" />
              <Button onClick={handleAddGroup} disabled={groupProcessing === "new" || !newGroupName.trim()}>
                {groupProcessing === "new" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Add Group
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Group Name</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupsLoading ? (
                      <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                    ) : groups.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No groups yet</TableCell></TableRow>
                    ) : (
                      groups.map((g) => (
                        <TableRow key={g.id}>
                          <TableCell>{g.sort_order}</TableCell>
                          <TableCell className="font-medium">{g.name}</TableCell>
                          <TableCell className="text-sm">{new Date(g.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => { setEditingGroup(g); setEditGroupName(g.name); }}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="ghost" disabled={groupProcessing === g.id}>
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete "{g.name}"?</AlertDialogTitle>
                                    <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteGroup(g.id)}>Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Edit Group Dialog */}
          <Dialog open={!!editingGroup} onOpenChange={(open) => !open && setEditingGroup(null)}>
            <DialogContent>
              <DialogHeader><DialogTitle>Edit Group</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Group Name</Label>
                  <Input value={editGroupName} onChange={(e) => setEditGroupName(e.target.value)} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingGroup(null)}>Cancel</Button>
                <Button onClick={handleUpdateGroup} disabled={groupProcessing === editingGroup?.id}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Feedback Management</h2>
              <Button onClick={handleCreateLink} disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Generate Feedback Link
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Generate one-time feedback links to send to students. Once they submit, approve to show on website.
            </p>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Feedback</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feedbackLoading ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                    ) : feedbacks.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No feedbacks yet</TableCell></TableRow>
                    ) : (
                      feedbacks.map((f) => (
                        <TableRow key={f.id}>
                          <TableCell className="font-medium">{f.student_name || "—"}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{f.feedback_text || <span className="text-muted-foreground italic">Pending submission</span>}</TableCell>
                          <TableCell>
                            {f.is_submitted ? (
                              <div className="flex gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <Star key={i} className={`w-3 h-3 ${i < f.rating ? "fill-primary text-primary" : "text-muted-foreground/30"}`} />
                                ))}
                              </div>
                            ) : "—"}
                          </TableCell>
                          <TableCell>
                            {!f.is_submitted ? (
                              <Badge variant="outline">Pending</Badge>
                            ) : f.is_approved ? (
                              <Badge variant="default">Approved</Badge>
                            ) : (
                              <Badge variant="secondary">Submitted</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{new Date(f.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {!f.is_submitted && (
                                <Button size="sm" variant="ghost" onClick={() => copyLink(f.token)} title="Copy link">
                                  <Copy className="w-4 h-4" />
                                </Button>
                              )}
                              {f.is_submitted && (
                                <>
                                  <Button
                                    size="sm"
                                    variant={f.is_approved ? "outline" : "default"}
                                    onClick={() => handleToggleApproval(f.id, f.is_approved)}
                                    disabled={processingId === f.id}
                                    title={f.is_approved ? "Hide from website" : "Approve"}
                                  >
                                    {f.is_approved ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => openEdit(f)} title="Edit">
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="ghost" disabled={processingId === f.id}>
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this feedback?</AlertDialogTitle>
                                    <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteFeedback(f.id)}>Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Edit Feedback Dialog */}
          <Dialog open={!!editFeedback} onOpenChange={(open) => !open && setEditFeedback(null)}>
            <DialogContent>
              <DialogHeader><DialogTitle>Edit Feedback</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Student Name</Label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Feedback Text</Label>
                  <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={4} />
                </div>
                <div className="space-y-2">
                  <Label>Rating</Label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((r) => (
                      <button key={r} type="button" onClick={() => setEditRating(r)}>
                        <Star className={`w-5 h-5 ${r <= editRating ? "fill-primary text-primary" : "text-muted-foreground/30"}`} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditFeedback(null)}>Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={processingId === editFeedback?.id}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GroupsAndFeedbackTab;
